<template>
    <ValidationObserver ref="observer" tag="div">
        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:gap-y-6">
            <!-- Fila 1: Nombre, Apellidos, Tipo Documento -->
            <BaseInputText
                v-model="formData.nombre"
                label="Nombre *"
                name="nombre"
                rules="required"
            />
            <BaseInputText
                v-model="formData.apellidos"
                label="Apellidos *"
                name="apellidos"
                rules="required"
            />
            <DocumentTypePicker
                :value="formData.tipoDocumento"
                @input="formData.tipoDocumento = $event"
            />

            <!-- Fila 2: DNI, Fecha Nacimiento, Fecha Caducidad DNI -->
            <DocumentInput
                :value="formData.dni"
                :document-type="formData.tipoDocumento"
                @input="formData.dni = $event"
            />
            <BaseDateInput
                v-model="formData.fecha_nacimiento"
                label="Fecha de nacimiento *"
                name="Fecha de nacimiento"
                rules="required"
                data-cy="fecha-nacimiento"
            />
            <BaseDateInput
                v-model="formData.fecha_caducidad_dni"
                label="Fecha caducidad DNI *"
                name="Fecha caducidad DNI"
                rules="required"
                data-cy="fecha-caducidad-dni"
            />

            <!-- Fila 3: Teléfono y Email -->
            <PhoneInput
                :value="formData.telefono"
                :error="phoneError"
                data-cy="telefono-jugador"
                @input="onPhoneUpdate"
                @update:callingCode="code => formData.codigo_pais = code"
                @update:isValid="onPhoneValidityChange"
            />
            <BaseInputText
                v-model="formData.email"
                label="Email *"
                name="email"
                rules="required|email"
            />

            <!-- Fila 4: País → Provincia → Localidad (condicional España) -->
            <BaseMultiselect
                :value="ubicacion.paisId.value"
                :options="ubicacion.paises.value"
                label="País *"
                name="pais"
                value-key="id"
                label-key="nombre"
                rules="required"
                :loading="ubicacion.loadingPaises.value"
                @input="onPaisSelect"
            />
            <BaseMultiselect
                v-if="ubicacion.esEspana.value"
                :value="ubicacion.provinciaId.value"
                :options="ubicacion.provincias.value"
                label="Provincia *"
                name="provincia"
                value-key="id"
                label-key="nombre"
                rules="required"
                :loading="ubicacion.loadingProvincias.value"
                @input="onProvinciaSelect"
            />
            <BaseMultiselect
                v-if="ubicacion.esEspana.value && ubicacion.provinciaId.value"
                :value="ubicacion.localidadId.value"
                :options="ubicacion.localidades.value"
                label="Localidad *"
                name="localidad"
                value-key="id"
                label-key="nombre"
                rules="required"
                :loading="ubicacion.loadingLocalidades.value"
                @input="onLocalidadSelect"
            />

            <!-- Fila 5: Nacionalidad, Dirección, Club -->
            <BaseInputText
                v-model="formData.nacionalidad"
                label="Nacionalidad *"
                name="nacionalidad"
                rules="required"
            />
            <BaseInputText
                v-model="formData.direccion"
                label="Dirección *"
                name="direccion"
                rules="required"
            />
            <BaseInputText
                v-model="formData.club_de_procedencia"
                label="Club de procedencia"
                name="club_de_procedencia"
            />

            <!-- Fila 6: Categoría Base, Equipo, Lateralidad -->
            <BaseMultiselect
                :value="selectedCategoria"
                :options="categorias"
                label="Categoría base *"
                name="categoría base"
                rules="required"
                label-key="name"
                value-key="id"
                :multiple="false"
                @input="onCategoriaSelect"
            />
            <BaseMultiselect
                v-if="equipos.length"
                :value="selectedEquipo"
                :options="equipos"
                label="Equipo *"
                name="equipo"
                rules="required"
                label-key="nombre"
                value-key="id"
                :multiple="false"
                @input="onEquipoSelect"
            />
            <BaseMultiselect
                :value="selectedLateralidad"
                :options="lateralidadOptions"
                label="Lateralidad *"
                name="lateralidad"
                rules="required"
                label-key="name"
                value-key="id"
                :multiple="false"
                @input="onLateralidadSelect"
            />

            <!-- ── Custom fields configurables para el deportista ── -->
            <template v-if="fields && fields.length > 0">
                <div v-for="field in fields" :key="field.name" class="tw:col-span-1">
                    <FieldComponent
                        :field="field"
                        v-model="formData.fields_deportista[field.name]"
                    />
                </div>
            </template>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, inject, watch, onMounted } from 'vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue';
    import DocumentTypePicker from '@/modules/admin/ui/DocumentTypePicker.vue';
    import DocumentInput from '@/modules/admin/ui/DocumentInput.vue';
    import PhoneInput from '@/modules/admin/ui/PhoneInput.vue';
    import FieldComponent from '@/components/common/FieldComponent.vue';
    import { useUbicacion, SPAIN_ID } from '@/composables/admin/common/useUbicacion';

    const formData = inject('formData');

    const props = defineProps({
        categorias: { type: Array, default: () => [] },
        equipos: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        submitting: { type: Boolean, default: false },
        phoneValid: { type: Boolean, default: false },
        fields: { type: Array, default: () => [] },
    });

    const emit = defineEmits(['phone-valid-change', 'categoria-change']);

    // ── ValidationObserver ref (expuesto al padre para validación) ──
    const observer = ref(null);

    // ── Lateralidad como objetos para BaseMultiselect ──
    const lateralidadOptions = [
        { id: 1, name: 'Diestro' },
        { id: 2, name: 'Zurdo' },
        { id: 3, name: 'Ambidiestro' },
    ];

    // ── Selecciones locales para BaseMultiselect (sincronizan con formData IDs) ──
    const selectedCategoria = ref(null);
    const selectedEquipo = ref(null);
    const selectedLateralidad = ref(null);

    // ── Handlers para BaseMultiselect ──
    //   BaseMultiselect emite el ID (número) en @input, no el objeto completo.
    function onCategoriaSelect(id) {
        selectedCategoria.value = id;
        formData.categoria_base_id = id ?? null;
        // Buscar objeto completo para el emit categoria-change
        const cat = props.categorias.find(c => c.id === id) || null;
        emit('categoria-change', cat);
    }

    function onEquipoSelect(id) {
        selectedEquipo.value = id;
        formData.equipo_id = id ?? null;
    }

    function onLateralidadSelect(id) {
        selectedLateralidad.value = id;
        const opt = lateralidadOptions.find(l => l.id === id);
        formData.lateralidad = opt?.name ?? null;
    }

    // ── Handlers para PhoneInput ──
    const phoneError = ref('');
    const phoneValidLocal = ref(false);

    function onPhoneUpdate(val) {
        formData.telefono = val;
        phoneError.value = '';
    }

    function onPhoneValidityChange(isValid) {
        phoneValidLocal.value = isValid;
        emit('phone-valid-change', isValid);
    }

    // ── Ubicación (País → Provincia → Localidad) ──
    const ubicacion = useUbicacion();

    function onPaisSelect(id) {
        const paisObj = ubicacion.paises.value.find(p => p.id === id) || null;
        formData.pais_de_nacimiento = paisObj;
        ubicacion.seleccionarPais(id);
        formData.provincia_nacimiento_id = null;
        formData.localidad_nacimiento_id = null;
    }

    function onProvinciaSelect(id) {
        formData.provincia_nacimiento_id = id;
        ubicacion.seleccionarProvincia(id);
        formData.localidad_nacimiento_id = null;
    }

    function onLocalidadSelect(id) {
        formData.localidad_nacimiento_id = id;
        ubicacion.seleccionarLocalidad(id);
    }

    // ── Inicializar ubicación con valores existentes ──
    //    Primero carga los países, luego usa inicializar() para la cascada completa.
    onMounted(async() => {
        await ubicacion.cargarPaises();

        const paisId = formData.pais_de_nacimiento?.id || SPAIN_ID;
        const paisObj = ubicacion.paises.value.find(p => p.id === paisId) || null;

        if (paisObj) {
            formData.pais_de_nacimiento = paisObj;
        }

        await ubicacion.inicializar(
            paisId,
            formData.provincia_nacimiento_id,
            formData.localidad_nacimiento_id,
        );

        if (paisId !== SPAIN_ID) {
            formData.provincia_nacimiento_id = null;
            formData.localidad_nacimiento_id = null;
        }

        // ── Sincronizar selectedCategoria si formData ya tiene categoría preseleccionada ──
        //   (renovar: buscarBase rellena formData ANTES de montar este paso)
        if (formData.categoria_base_id && !selectedCategoria.value) {
            const match = props.categorias.find(
                c => String(c.id) === String(formData.categoria_base_id)
            );
            if (match) {
                selectedCategoria.value = match.id;
            }
        }
    });

    // ── Sincronizar selectedCategoria si formData ya tiene categoría preseleccionada ──
    //   Cubre 2 casos:
    //   1. Una sola categoría → auto-seleccionar (flow crear sin pre-carga)
    //   2. Varias categorías + formData ya tiene categoria_base_id → preseleccionar
    //      (flow renovar: buscarBase rellena formData ANTES de montar este paso)
    watch(() => props.categorias, cats => {
        if (!selectedCategoria.value && cats.length) {
            if (cats.length === 1) {
                selectedCategoria.value = cats[0].id;
                formData.categoria_base_id = cats[0].id;
                emit('categoria-change', cats[0]);
            } else if (formData.categoria_base_id) {
                const match = cats.find(c => String(c.id) === String(formData.categoria_base_id));
                if (match) {
                    selectedCategoria.value = match.id;
                }
            }
        }
    }, { immediate: true });

    // ── Exponer observer para que el padre pueda validar ──
    //   IMPORTANTE: usamos getter para exponer el ValidationObserver directo,
    //   no el Ref. En Vue 2.7 <script setup>, defineExpose NO auto-unwrappea
    //   refs, así que sin esto el padre recibiría Ref{observer} y no podría
    //   llamar a .validate() directamente.
    defineExpose({
        get observer() { return observer.value; },
        async validate() {
            const formValid = await observer.value.validate();
            let phoneOk = true;
            if (!formData.telefono || !phoneValidLocal.value) {
                phoneError.value = 'El teléfono es requerido y debe ser válido';
                phoneOk = false;
            }
            return formValid && phoneOk;
        },
    });
</script>
